import { exactHash, thumbnailBlobForFile } from "./metrics";
import { hasHeicHint } from "./image-decode";

export type DetectorMode = "face" | "person";
type Status = "found" | "uncertain" | "none" | "error";
type ScanItem = { path: string; file: File; handle?: FileSystemFileHandle; parentHandle?: FileSystemDirectoryHandle };
type ScanResult = ScanItem & {
  status: Status;
  score: number;
  detail?: string;
  faceScore?: number;
  ultraFaceScore?: number;
  personScore?: number;
  poseScore?: number;
  rotationDegrees?: number;
  evidence?: string;
  automaticStatus?: Status;
  manualDecision?: "no-person" | "person";
  quarantineStatus?: "moving" | "moved" | "source-retained" | "failed";
  quarantineName?: string;
  quarantineError?: string;
  previewUrl?: string;
};

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp", "bmp", "gif", "tif", "tiff", "avif", "heic", "heif"]);
const state: {
  folder?: FileSystemDirectoryHandle;
  selectionLabel?: string;
  items: ScanItem[];
  results: ScanResult[];
  scanning: boolean;
  mode: DetectorMode;
  filter: Status;
  visible: number;
  error?: string;
  worker?: Worker;
  quarantineFolder?: FileSystemDirectoryHandle;
  selectedPaths: Set<string>;
  batchProgress?: { current: number; total: number };
} = { items: [], results: [], scanning: false, mode: "face", filter: "none", visible: 60, selectedPaths: new Set() };

const previewUrls = new Set<string>();
const heicPreviewLoads = new Set<string>();
const escape = (value: string) => value.replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character]!);

function clearHeicPreviews(): void {
  for (const result of state.results) {
    if (result.previewUrl) URL.revokeObjectURL(result.previewUrl);
    result.previewUrl = undefined;
  }
  heicPreviewLoads.clear();
}

function canQuarantine(result: ScanResult): boolean {
  return (state.mode === "person" || result.status === "error")
    && result.quarantineStatus !== "moved"
    && result.quarantineStatus !== "source-retained";
}

function selectedQuarantineResults(): ScanResult[] {
  return state.results.filter((result) => state.selectedPaths.has(result.path) && canQuarantine(result) && result.handle);
}

async function discover(directory: FileSystemDirectoryHandle, prefix: string, output: ScanItem[]): Promise<void> {
  for await (const handle of directory.values()) {
    const path = prefix ? `${prefix}/${handle.name}` : handle.name;
    if (handle.kind === "directory") { await discover(handle, path, output); continue; }
    if (!IMAGE_EXTENSIONS.has(handle.name.split(".").pop()?.toLowerCase() ?? "")) continue;
    output.push({ path, file: await handle.getFile(), handle, parentHandle: directory });
  }
}

function card(result: ScanResult): string {
  const url = result.previewUrl ?? URL.createObjectURL(result.file);
  if (!result.previewUrl) previewUrls.add(url);
  const heicPreview = hasHeicHint(result.file) && !result.previewUrl ? ` data-heic-preview="${escape(result.path)}"` : "";
  const label = result.manualDecision === "no-person" ? "Manually marked: no person" : result.manualDecision === "person" ? "Manually marked: person" : result.status === "none" ? state.mode === "face" ? "No face detected" : "No person detected" : result.status === "uncertain" ? "Needs review" : result.status === "found" ? state.mode === "face" ? "Face detected" : "Person or face detected" : "Not analyzed";
  const faceScores = `YuNet ${result.faceScore === undefined ? "—" : `${Math.round(result.faceScore * 100)}%`} · UltraFace ${result.ultraFaceScore === undefined ? "not run" : `${Math.round(result.ultraFaceScore * 100)}%`}${result.rotationDegrees ? ` · face view rotated ${result.rotationDegrees}°` : ""}`;
  const evidence = state.mode === "person" && result.status !== "error"
    ? `<small>${escape(result.evidence ?? "Model evidence requires review")}</small><small>Raw model scores (not probabilities): YOLOX ${result.personScore === undefined ? "—" : `${Math.round(result.personScore * 100)}%`} · pose ${result.poseScore === undefined ? "not run" : `${Math.round(result.poseScore * 100)}%`} · ${faceScores}</small>`
    : result.status === "error" ? `<small>${escape(result.detail ?? "Unable to decode")}</small>` : `<small>${escape(result.evidence ?? "Model evidence requires review")}</small><small>Raw model scores (not probabilities): ${faceScores}</small>`;
  const quarantineAllowed = canQuarantine(result) || result.quarantineStatus === "moved" || result.quarantineStatus === "source-retained";
  const quarantine = quarantineAllowed ? result.quarantineStatus === "moved"
    ? `<div class="detector-quarantine-result success">Moved safely as ${escape(result.quarantineName ?? result.file.name)}</div>`
    : result.quarantineStatus === "source-retained"
      ? `<div class="detector-quarantine-result warning">Verified copy saved as ${escape(result.quarantineName ?? result.file.name)}, but the original could not be removed.</div>`
      : `<button type="button" class="detector-quarantine-button" data-detector-quarantine="${escape(result.path)}" ${!result.handle || result.quarantineStatus === "moving" || state.batchProgress ? "disabled" : ""}>${!result.handle ? "Folder scan required to quarantine" : result.quarantineStatus === "moving" ? "Moving safely…" : result.quarantineStatus === "failed" ? "Retry quarantine" : result.status === "error" ? "Quarantine without analysis" : "Quarantine"}</button>${result.quarantineError ? `<div class="detector-quarantine-result warning">${escape(result.quarantineError)}</div>` : ""}`
    : "";
  const selection = canQuarantine(result)
    ? `<label class="detector-select"><input type="checkbox" data-detector-select="${escape(result.path)}" ${state.selectedPaths.has(result.path) ? "checked" : ""} ${!result.handle || result.quarantineStatus === "moving" || state.batchProgress ? "disabled" : ""}><span>${result.handle ? "Select for quarantine" : "Folder scan required for selection"}</span></label>`
    : "";
  const review = state.mode === "person" && result.status !== "error" && result.quarantineStatus !== "moved"
    ? `<button type="button" class="secondary-button detector-review-button" data-detector-review="${escape(result.path)}">${result.manualDecision ? "Undo manual decision" : result.status === "none" ? "Mark as person" : "Mark as no person"}</button>` : "";
  return `<article class="detector-card"><div class="detector-preview"><img src="${url}" alt="" loading="lazy"${heicPreview}></div><div class="detector-card-body"><strong>${escape(result.file.name)}</strong><span title="${escape(result.path)}">${escape(result.path)}</span><b class="detector-status ${result.status}">${label}</b>${evidence}${review}${selection}${quarantine}</div></article>`;
}

async function hydrateHeicPreviews(): Promise<void> {
  const images = [...document.querySelectorAll<HTMLImageElement>("[data-heic-preview]")];
  for (const image of images) {
    const path = image.dataset.heicPreview;
    if (!path || heicPreviewLoads.has(path)) continue;
    const result = state.results.find((item) => item.path === path);
    if (!result || result.previewUrl) continue;
    heicPreviewLoads.add(path);
    try {
      const blob = await thumbnailBlobForFile(result.file, 900);
      const url = URL.createObjectURL(blob);
      if (!state.results.includes(result)) { URL.revokeObjectURL(url); continue; }
      result.previewUrl = url;
      for (const current of document.querySelectorAll<HTMLImageElement>("[data-heic-preview]")) {
        if (current.dataset.heicPreview === path) current.src = url;
      }
    } catch {
      // Scan status already explains a decode failure; preview failure alone
      // must never be interpreted as damage or trigger a file operation.
    } finally {
      heicPreviewLoads.delete(path);
    }
  }
}

export function detectorView(mode: DetectorMode): string {
  for (const url of previewUrls) URL.revokeObjectURL(url);
  previewUrls.clear();
  if (state.mode !== mode) {
    clearHeicPreviews();
    state.worker?.terminate(); state.worker = undefined; state.scanning = false;
    state.items = []; state.results = []; state.error = undefined; state.visible = 60; state.filter = "none"; state.selectionLabel = undefined; state.folder = undefined; state.quarantineFolder = undefined; state.selectedPaths.clear(); state.batchProgress = undefined;
  }
  state.mode = mode;
  const label = mode === "face" ? "face" : "person";
  const shown = state.results.filter((item) => item.status === state.filter);
  const selectableShown = shown.filter((item) => canQuarantine(item) && item.handle);
  const selectedCount = selectedQuarantineResults().length;
  const showBatchControls = state.mode === "person" || shown.some((item) => item.status === "error");
  const support = window.isSecureContext && "Worker" in window;
  const folderSupport = support && "showDirectoryPicker" in window;
  const counts = (status: Status) => state.results.filter((item) => item.status === status).length;
  return `<main class="detector-page"><section class="detector-hero"><p class="eyebrow">Local AI photo review</p><h1>${mode === "face" ? "Face detector" : "Person detector"}</h1><p class="intro-copy">${mode === "face" ? "Find photos where no face was detected. Review unreadable files and optionally move them to a quarantine folder you choose." : "Find photos where neither a person nor a face was detected. Review each result, then optionally move individual files to a quarantine folder you choose."}</p></section>
    <section class="workspace"><div class="section-heading"><div><p class="step">Step 1</p><h2>Choose one photo folder</h2></div><span class="quiet">Subfolders are included</span></div>
      <div class="detector-controls">${folderSupport ? `<button class="secondary-button" id="detector-folder" ${state.scanning ? "disabled" : ""}>${state.folder ? `Folder: ${escape(state.folder.name)}` : "Choose folder"}</button>` : ""}<label class="secondary-button detector-file-label" for="detector-files">${state.selectionLabel ? escape(state.selectionLabel) : "Or choose photos"}</label><input id="detector-files" type="file" accept="image/*,.heic,.heif,.tif,.tiff" multiple hidden ${state.scanning ? "disabled" : ""}><button class="primary-button" id="detector-start" ${(!state.folder && !state.items.length) || state.scanning || !support ? "disabled" : ""}>${state.scanning ? "Scanning locally…" : `Find photos without ${label === "face" ? "faces" : "people"}`}</button>${state.scanning ? '<button class="secondary-button" id="detector-stop">Stop</button>' : ""}</div>
      ${!support ? '<p class="detector-note">Use a current browser over HTTPS.</p>' : ""}
      ${state.error ? `<div class="notice error" role="alert">${escape(state.error)}</div>` : ""}
      ${state.scanning ? `<div class="progress-block"><div><strong>Scanning locally</strong><span id="detector-progress">${state.results.length} / ${state.items.length}</span></div><div class="progress-track"><span id="detector-progress-bar" style="width:${state.items.length ? Math.round(state.results.length / state.items.length * 100) : 0}%"></span></div><p id="detector-current">${escape(state.results.at(-1)?.path ?? "Loading models…")}</p></div>` : ""}
      <p class="detector-note">Models: ${mode === "face" ? "YuNet + UltraFace (MIT)" : "YOLOX-S + MediaPipe Pose (Apache 2.0), YuNet + UltraFace (MIT)"}. A face is confirmed only when both face models agree on its location. Sideways face candidates are checked after an in-memory rotation; original files stay unchanged. In Person detector, a hand alone does not count as a person; a single model's body candidate is marked for review, not confirmed. Scores are not probabilities.</p>
    </section>
    ${state.results.length ? `<section class="results"><div class="section-heading"><div><p class="step">Step 2</p><h2>Review results</h2></div><button class="secondary-button" id="detector-csv">Download CSV</button></div>
      <div class="detector-filters">${(["none", "uncertain", "found", "error"] as Status[]).map((status) => `<button type="button" class="${state.filter === status ? "active" : ""}" data-detector-filter="${status}">${status === "none" ? `No ${label}` : status === "uncertain" ? "Needs review" : status === "found" ? "Detected" : "Errors"} <b>${counts(status)}</b></button>`).join("")}</div>
      ${showBatchControls ? `<div class="detector-batch-actions"><div><strong><span data-detector-selected-count>${selectedCount}</span> selected</strong><span>Select all applies to every eligible item in the current “${state.filter === "none" ? `No ${label}` : state.filter === "uncertain" ? "Needs review" : state.filter === "found" ? "Detected" : "Errors"}” category.</span></div><div><button type="button" class="secondary-button" id="detector-select-all" ${!selectableShown.length || state.batchProgress ? "disabled" : ""}>Select all (${selectableShown.length})</button><button type="button" class="secondary-button" id="detector-clear-selection" ${!selectedCount || state.batchProgress ? "disabled" : ""}>Clear selection</button><button type="button" class="danger-button" id="detector-quarantine-selected" ${!selectedCount || state.batchProgress ? "disabled" : ""}>${state.batchProgress ? `Quarantining ${state.batchProgress.current} / ${state.batchProgress.total}…` : `Quarantine all selected (${selectedCount})`}</button></div></div>` : ""}
      <p class="detector-note">Nothing is selected or quarantined automatically. “No ${label}” means the models found no evidence, not a guarantee. Review before moving or deleting anything.${mode === "person" ? " You can manually correct a result; the correction stays in this page and is included in CSV." : ""} “Not analyzed” means decoding failed; it does not mean the file is damaged. Such files can be quarantined only after you choose them. Quarantine first writes and SHA-256 verifies a safe, non-overwriting copy; only then is the source removed.</p>
      ${shown.length ? `<div class="detector-grid">${shown.slice(0, state.visible).map(card).join("")}</div>${shown.length > state.visible ? `<button class="secondary-button detector-more" id="detector-more">Show more (${shown.length - state.visible} remaining)</button>` : ""}` : '<div class="empty-state">No photos in this category.</div>'}
    </section>` : ""}
  </main>`;
}

function downloadCsv(): void {
  const header = ["path", "status", "automatic_status", "manual_decision", "highest_score", "yunet_score", "ultraface_score", "face_rotation_degrees", "person_score", "pose_score", "evidence", "quarantine_status", "quarantine_name", "error"];
  const quote = (value: string) => `"${value.replaceAll('"', '""')}"`;
  const rows = state.results.map((item) => [item.path, item.status, item.automaticStatus ?? item.status, item.manualDecision ?? "", item.score.toFixed(4), item.faceScore?.toFixed(4) ?? "", item.ultraFaceScore?.toFixed(4) ?? "", item.rotationDegrees?.toString() ?? "", item.personScore?.toFixed(4) ?? "", item.poseScore?.toFixed(4) ?? "", item.evidence ?? "", item.quarantineStatus ?? "", item.quarantineName ?? "", item.quarantineError ?? item.detail ?? ""].map(quote).join(","));
  const blob = new Blob(["\ufeff", header.join(","), "\r\n", rows.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `phototidy-${state.mode}-scan-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function chooseFolder(render: () => void): Promise<void> {
  try {
    clearHeicPreviews();
    state.folder = await window.showDirectoryPicker({ id: "phototidy-detector", mode: "readwrite" });
    state.results = []; state.items = []; state.selectionLabel = undefined; state.error = undefined; state.quarantineFolder = undefined; state.selectedPaths.clear(); state.batchProgress = undefined; render();
  } catch (error) {
    if (!(error instanceof DOMException && error.name === "AbortError")) { state.error = error instanceof Error ? error.message : String(error); render(); }
  }
}

async function startScan(mode: DetectorMode, render: () => void): Promise<void> {
  if ((!state.folder && !state.items.length) || state.scanning) return;
  clearHeicPreviews();
  state.scanning = true; state.results = []; if (state.folder) state.items = []; state.filter = "none"; state.visible = 60; state.error = undefined; state.selectedPaths.clear(); state.batchProgress = undefined; render();
  try {
    if (state.folder) await discover(state.folder, "", state.items);
    if (!state.items.length) { state.error = "No supported image files were found in this folder."; state.scanning = false; render(); return; }
    const worker = new Worker(new URL("./detector.worker.ts", import.meta.url), { type: "module" });
    state.worker = worker;
    worker.onmessage = (event: MessageEvent) => {
      if (state.worker !== worker) return;
      if (event.data.type === "result") {
        const item = state.items[event.data.index];
        if (!item) return;
        state.results.push({ ...item, status: event.data.status, automaticStatus: event.data.status, score: event.data.score, detail: event.data.detail, faceScore: event.data.faceScore, ultraFaceScore: event.data.ultraFaceScore, personScore: event.data.personScore, poseScore: event.data.poseScore, rotationDegrees: event.data.rotationDegrees, evidence: event.data.evidence });
        const current = document.querySelector<HTMLElement>("#detector-current");
        if (current) current.textContent = item.path;
        const progress = document.querySelector<HTMLElement>("#detector-progress");
        if (progress) progress.textContent = `${state.results.length} / ${state.items.length}`;
        const bar = document.querySelector<HTMLElement>("#detector-progress-bar");
        if (bar) bar.style.width = `${Math.round(state.results.length / state.items.length * 100)}%`;
      } else if (event.data.type === "done") {
        state.scanning = false; state.worker?.terminate(); state.worker = undefined; render();
      }
    };
    worker.onerror = (event) => { if (state.worker !== worker) return; state.error = event.message || "Detector worker failed."; state.scanning = false; worker.terminate(); state.worker = undefined; render(); };
    worker.postMessage({ type: "scan", mode, files: state.items });
    render();
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
    state.scanning = false; render();
  }
}

async function hasPermission(handle: FileSystemHandle, mode: "read" | "readwrite"): Promise<boolean> {
  if (await handle.queryPermission({ mode }) === "granted") return true;
  return await handle.requestPermission({ mode }) === "granted";
}

async function availableQuarantineName(directory: FileSystemDirectoryHandle, wanted: string): Promise<string> {
  const dot = wanted.lastIndexOf(".");
  const stem = dot > 0 ? wanted.slice(0, dot) : wanted;
  const extension = dot > 0 ? wanted.slice(dot) : "";
  for (let attempt = 0; attempt < 10_000; attempt += 1) {
    const candidate = attempt === 0 ? wanted : `${stem}_phototidy_${attempt}${extension}`;
    try {
      await directory.getFileHandle(candidate);
    } catch (error) {
      if (error instanceof DOMException && error.name === "NotFoundError") return candidate;
      throw error;
    }
  }
  throw new Error(`Unable to find a safe destination name for ${wanted}.`);
}

async function quarantineResult(result: ScanResult, render: () => void): Promise<void> {
  if (!result.handle || !result.parentHandle || result.quarantineStatus === "moving" || result.quarantineStatus === "moved" || result.quarantineStatus === "source-retained") return;
  result.quarantineStatus = "moving";
  result.quarantineError = undefined;
  render();
  let destination: FileSystemFileHandle | undefined;
  let targetName: string | undefined;
  try {
    if (!state.quarantineFolder) {
      state.quarantineFolder = await window.showDirectoryPicker({ id: "phototidy-detector-quarantine", mode: "readwrite" });
    }
    if (!await hasPermission(result.parentHandle, "readwrite")) throw new Error("Write access to the source folder was not granted.");
    if (!await hasPermission(state.quarantineFolder, "readwrite")) throw new Error("Write access to the quarantine folder was not granted.");
    if (await result.parentHandle.isSameEntry(state.quarantineFolder)) throw new Error("Choose a different folder for quarantine.");

    targetName = await availableQuarantineName(state.quarantineFolder, result.file.name);
    destination = await state.quarantineFolder.getFileHandle(targetName, { create: true });
    const sourceHash = await exactHash(result.file);
    const writable = await destination.createWritable();
    try {
      await writable.write(result.file);
      await writable.close();
    } catch (error) {
      await writable.abort().catch(() => undefined);
      throw error;
    }
    const copied = await destination.getFile();
    if (copied.size !== result.file.size || await exactHash(copied) !== sourceHash) throw new Error("SHA-256 verification failed; the source was left untouched.");

    result.quarantineName = targetName;
    try {
      await result.parentHandle.removeEntry(result.file.name);
      result.quarantineStatus = "moved";
    } catch {
      result.quarantineStatus = "source-retained";
      result.quarantineError = "The verified quarantine copy exists, but the original is still in its source folder.";
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      result.quarantineStatus = undefined;
    } else {
      result.quarantineStatus = "failed";
      result.quarantineError = error instanceof Error ? error.message : "Quarantine failed safely; the source was not removed.";
      if (destination && targetName && state.quarantineFolder) await state.quarantineFolder.removeEntry(targetName).catch(() => undefined);
    }
  }
  if (result.quarantineStatus === "moved" || result.quarantineStatus === "source-retained") state.selectedPaths.delete(result.path);
  render();
}

async function quarantineSelected(render: () => void): Promise<void> {
  const selected = selectedQuarantineResults();
  if (!selected.length || state.batchProgress) return;
  if (!state.quarantineFolder) {
    try {
      state.quarantineFolder = await window.showDirectoryPicker({ id: "phototidy-detector-quarantine", mode: "readwrite" });
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        state.error = error instanceof Error ? error.message : String(error);
        render();
      }
      return;
    }
  }
  state.batchProgress = { current: 0, total: selected.length };
  render();
  for (let index = 0; index < selected.length; index += 1) {
    state.batchProgress.current = index + 1;
    await quarantineResult(selected[index], render);
  }
  state.batchProgress = undefined;
  render();
}

export function bindDetectorEvents(mode: DetectorMode, render: () => void): void {
  void hydrateHeicPreviews();
  document.querySelector("#detector-folder")?.addEventListener("click", () => void chooseFolder(render));
  document.querySelector<HTMLInputElement>("#detector-files")?.addEventListener("change", (event) => {
    clearHeicPreviews();
    const files = [...((event.target as HTMLInputElement).files ?? [])].filter((file) => IMAGE_EXTENSIONS.has(file.name.split(".").pop()?.toLowerCase() ?? ""));
    state.items = files.map((file) => ({ path: file.name, file })); state.folder = undefined; state.quarantineFolder = undefined; state.selectedPaths.clear(); state.batchProgress = undefined;
    state.selectionLabel = `${files.length} photo${files.length === 1 ? "" : "s"} selected`;
    state.results = []; state.error = files.length ? undefined : "No supported image files were selected."; render();
  });
  document.querySelector("#detector-start")?.addEventListener("click", () => void startScan(mode, render));
  document.querySelector("#detector-stop")?.addEventListener("click", () => { state.worker?.terminate(); state.worker = undefined; state.scanning = false; state.error = "Scan stopped. Partial results are shown below."; render(); });
  document.querySelector("#detector-csv")?.addEventListener("click", downloadCsv);
  document.querySelector("#detector-more")?.addEventListener("click", () => { state.visible += 60; render(); });
  document.querySelectorAll<HTMLButtonElement>("[data-detector-filter]").forEach((button) => button.addEventListener("click", () => { state.filter = button.dataset.detectorFilter as Status; state.visible = 60; render(); }));
  document.querySelector("#detector-select-all")?.addEventListener("click", () => {
    for (const result of state.results) if (result.status === state.filter && canQuarantine(result) && result.handle) state.selectedPaths.add(result.path);
    render();
  });
  document.querySelector("#detector-clear-selection")?.addEventListener("click", () => { state.selectedPaths.clear(); render(); });
  document.querySelector("#detector-quarantine-selected")?.addEventListener("click", () => void quarantineSelected(render));
  document.querySelectorAll<HTMLInputElement>("[data-detector-select]").forEach((checkbox) => checkbox.addEventListener("change", () => {
    const path = checkbox.dataset.detectorSelect;
    if (!path) return;
    if (checkbox.checked) state.selectedPaths.add(path); else state.selectedPaths.delete(path);
    render();
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-detector-quarantine]").forEach((button) => button.addEventListener("click", () => {
    const result = state.results.find((item) => item.path === button.dataset.detectorQuarantine);
    if (result) void quarantineResult(result, render);
  }));
  if (mode === "person") document.querySelectorAll<HTMLButtonElement>("[data-detector-review]").forEach((button) => button.addEventListener("click", () => {
    const result = state.results.find((item) => item.path === button.dataset.detectorReview);
    if (!result) return;
    if (result.manualDecision) {
      result.manualDecision = undefined;
      result.status = result.automaticStatus ?? result.status;
    } else {
      result.manualDecision = result.status === "none" ? "person" : "no-person";
      result.status = result.manualDecision === "person" ? "found" : "none";
    }
    state.filter = result.status;
    render();
  }));
}

export function stopDetectorScan(): void {
  state.worker?.terminate();
  state.worker = undefined;
  state.scanning = false;
  clearHeicPreviews();
}
